import { useState, useEffect, forwardRef } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
// Constants - Functions
import { WEB_SERVICE_MIMS } from "../../../../config/constants";
import { checkIfItIsTheCurrentWeek } from "../../../../config/functions";
// Components
import { NoPrescriptionsDiv, NoPrescriptionsText } from "./styles";
import UseFetch from "../../../../config/useFetch";
import Loader from "../../../loader";
import Title from "../../../title";
import Weeks from "./sections/weeks";
import Prescriptions from "./sections/prescriptions";
import WeeklySummary from "./sections/weeklySummary";
import ActivitiesPerformed from "./sections/activitiesPerformed";

const Activities = forwardRef(
  (
    {
      startingDate,
      trainingHasStarted,
      hasRecsys,
      isLoadingPrescriptions,
      setIsLoadingPrescriptions,
      isSomePrescriptionInserted,
      setIsSomePrescriptionInserted,
      isShowingPrescriptionsPopup,
      setIsShowingPrescriptionsPopup,
    },
    ref
  ) => {
    const [weeks] = useState(Array.from({ length: 12 }, (_, i) => i));
    const [datesPerWeek, setDatesPerWeek] = useState([]);
    const [targetedWeek, setTargetedWeek] = useState(null);
    const [isEditableWeek, setIsEditableWeek] = useState(true);
    const [prescriptions, setPrescriptions] = useState([]);
    const [activitiesPerformed, setActivitiesPerformed] = useState([]);
    const [refreshPrescriptions, setRefreshPrescriptions] = useState(false);

    const { patientId } = useParams();

    // Set the date of each week
    useEffect(() => {
      const newDatesPerWeek = weeks.map((week) => {
        return week < 1
          ? moment(startingDate, "DD/MM/YYYY").format("DD-MM-YYYY")
          : moment(startingDate, "DD/MM/YYYY")
              .add(week * 7, "days")
              .format("DD-MM-YYYY");
      });

      setDatesPerWeek(newDatesPerWeek);

      return () => {
        setDatesPerWeek([]);
      };
    }, [weeks, startingDate]);

    // Set the current week
    useEffect(() => {
      if (!datesPerWeek.length) return;

      let newTargetedWeek = 0;
      datesPerWeek.forEach((date, i) => {
        if (checkIfItIsTheCurrentWeek(date)) newTargetedWeek = i;
      });

      // Set the last week if the training has finished
      const trainingEndingDate = moment(datesPerWeek.at(-1), "DD/MM/YYYY");
      const today = moment();
      if (today.isSameOrAfter(trainingEndingDate, "day")) {
        newTargetedWeek = weeks.length - 1;
        setIsEditableWeek(false);
      }

      setTargetedWeek(newTargetedWeek);

      return () => {
        setTargetedWeek(null);
        setIsEditableWeek(true);
      };
    }, [datesPerWeek, weeks]);

    // Fetch prescriptions
    useEffect(() => {
      if (targetedWeek === null || !datesPerWeek.length) return;

      setIsLoadingPrescriptions(true);

      const datesObj = {
        startingDate: moment(datesPerWeek[targetedWeek], "DD-MM-YYYY").format("YYYY-MM-DD"),
        endingDate: moment(datesPerWeek[targetedWeek], "DD-MM-YYYY").add(7, "days").format("YYYY-MM-DD"),
      };

      if (datesPerWeek.length && isEditableWeek) {
        UseFetch("post", `${WEB_SERVICE_MIMS}/recsys/get-current-prescriptions/${patientId}`, new AbortController(), {
          ...datesObj,
          hasRecsys: hasRecsys,
          isFirstWeek: targetedWeek === 0,
        })
          .then(({ data }) => {
            setIsSomePrescriptionInserted(data.isSomePrescriptionInserted);
            setPrescriptions(data.prescriptions);
            setIsLoadingPrescriptions(false);
          })
          .catch((error) => {
            console.log("Get current prescriptions data: ", error);
          });
      }

      if (trainingHasStarted && datesPerWeek.length && !isEditableWeek) {
        UseFetch("post", `${WEB_SERVICE_MIMS}/recsys/get-prescriptions-per-week/${patientId}`, new AbortController(), datesObj)
          .then(({ data }) => {
            setPrescriptions(data);
            setIsLoadingPrescriptions(false);
          })
          .catch((error) => {
            console.log("Get prescriptions data: ", error);
          });
      }

      return () => {
        setIsSomePrescriptionInserted(false);
        setPrescriptions([]);
        setIsLoadingPrescriptions(true);
      };
    }, [
      trainingHasStarted,
      patientId,
      datesPerWeek,
      targetedWeek,
      isEditableWeek,
      hasRecsys,
      refreshPrescriptions,
      setIsSomePrescriptionInserted,
      setIsLoadingPrescriptions,
    ]);

    // Fetch Activities Performed
    useEffect(() => {
      UseFetch("get", `${WEB_SERVICE_MIMS}/recsys/get-activities-performed/${patientId}`)
        .then(({ data }) => {
          setActivitiesPerformed(data);
        })
        .catch((error) => {
          console.log("Get Activities performed: ", error);
        });

      return () => {
        setActivitiesPerformed([]);
      };
    }, [patientId]);

    return (
      <div ref={ref} style={{ scrollMarginTop: "70px" }}>
        <Title title={"PATIENT FIT - ACTIVITIES"} marginBottom="15px" />
        <Weeks
          weeks={weeks}
          trainingHasStarted={trainingHasStarted}
          startingDate={startingDate}
          targetedWeek={targetedWeek}
          setTargetedWeek={setTargetedWeek}
          isEditableWeek={isEditableWeek}
          setIsEditableWeek={setIsEditableWeek}
          datesPerWeek={datesPerWeek}
        />
        {isLoadingPrescriptions ? (
          <Loader height={50} />
        ) : (
          <>
            {!Boolean(prescriptions.length) ? (
              <NoPrescriptionsDiv>
                <NoPrescriptionsText>There are no prescriptions this week</NoPrescriptionsText>
              </NoPrescriptionsDiv>
            ) : (
              <>
                <Prescriptions prescriptions={prescriptions} />
              </>
            )}
            <WeeklySummary
              hasRecsys={hasRecsys}
              prescriptions={prescriptions}
              isEditableWeek={isEditableWeek}
              isSomePrescriptionInserted={isSomePrescriptionInserted}
              setIsSomePrescriptionInserted={setIsSomePrescriptionInserted}
              datesPerWeek={datesPerWeek}
              targetedWeek={targetedWeek}
              startingDate={startingDate}
              refreshPrescriptions={refreshPrescriptions}
              setRefreshPrescriptions={setRefreshPrescriptions}
              isShowingPrescriptionsPopup={isShowingPrescriptionsPopup}
              setIsShowingPrescriptionsPopup={setIsShowingPrescriptionsPopup}
            />
            <ActivitiesPerformed activitiesPerformed={activitiesPerformed} />
          </>
        )}
      </div>
    );
  }
);

export default Activities;
